import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { stores as storesApi, ownerKyc } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Store as StoreIcon, ArrowRight, ScrollText, Check } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import ImageUploader from '../components/ImageUploader';
import TermsModal from '../components/TermsModal';

export default function BecomeSeller() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsAcceptedVersion, setTermsAcceptedVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '', category: 'kirana', description: '', village: '', address: '', phone: '',
    delivery_fee: 20, min_order: 100, image_url: ''
  });

  useEffect(() => {
    if (!user) return;
    ownerKyc.get().then(d => setTermsAcceptedVersion(d.terms_accepted_version || null)).catch(() => {});
  }, [user]);

  const submit = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!form.name || !form.village) { toast.error('Store name and village are required'); return; }
    if (!termsAcceptedVersion) { setTermsOpen(true); return; }
    setBusy(true);
    try {
      await storesApi.create({ ...form, delivery_fee: parseFloat(form.delivery_fee) || 0, min_order: parseFloat(form.min_order) || 0 });
      await refresh();
      toast.success('Store created!');
      nav('/dashboard');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-12">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#FFF3E0] text-[#D35400] grid place-items-center"><StoreIcon className="w-6 h-6"/></div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Open a store</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl">Sell on BharatCart</h1>
        </div>
      </div>
      <p className="text-ink/70 mt-3 max-w-xl">Bring your kirana, fancy or vegetable shop online in minutes. Receive orders, manage inventory and grow with your village.</p>
      <Link to="/partner/register" data-testid="become-seller-partner-link" className="mt-4 inline-flex items-center gap-2 rounded-xl border-2 border-[#E67E22] bg-[#FFF8EE] px-4 py-3 text-sm font-semibold text-[#D35400] hover:bg-[#FFE9CC] transition-colors">
        Manufacturer, brand owner or authorised distributor? Get verified and sell across India →
      </Link>

      {user && (
        <div data-testid="seller-terms-status" className={`mt-5 rounded-xl border p-3 flex items-center gap-3 ${termsAcceptedVersion ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
          <div className={`w-9 h-9 rounded-full grid place-items-center ${termsAcceptedVersion ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {termsAcceptedVersion ? <Check className="w-4 h-4"/> : <ScrollText className="w-4 h-4"/>}
          </div>
          <div className="flex-1 text-sm">
            {termsAcceptedVersion ? (
              <><b>Seller Agreement accepted</b> (Version {termsAcceptedVersion}). You may register your store.</>
            ) : (
              <><b>Seller Agreement pending.</b> Please read and accept the agreement before registering a store. This protects both you and your customers under Indian law.</>
            )}
          </div>
          {!termsAcceptedVersion && (
            <button data-testid="open-terms-btn" type="button" onClick={() => setTermsOpen(true)} className="rounded-full bg-[#D35400] hover:bg-[#B84300] text-white px-4 py-2 text-sm font-semibold">Read & accept</button>
          )}
        </div>
      )}

      <div className="card-soft p-6 mt-7 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Store name" testId="store-name-input"><input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="inp"/></Field>
        <Field label="Category">
          <select data-testid="store-category" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="inp">
            <option value="kirana">Kirana</option><option value="vegetables">Vegetables</option><option value="fancy">Fancy</option><option value="cloth">Cloth</option><option value="dairy">Dairy</option><option value="bakery">Bakery</option><option value="hotels">Hotels & Food</option><option value="medical">Medical</option><option value="electronics">Electronics</option><option value="electrical">Electrical</option><option value="mobiles">Mobiles</option><option value="books">Books</option><option value="artifacts">Artifacts & Crafts</option><option value="jewellery">Jewellery</option><option value="other">Other</option>
          </select>
        </Field>
        <Field label="Village / Town" testId="store-village-input"><input value={form.village} onChange={e=>setForm({...form, village:e.target.value})} className="inp"/></Field>
        <Field label="Phone" testId="store-phone-input"><input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} className="inp"/></Field>
        <div className="md:col-span-2"><Field label="Address" testId="store-address-input"><input value={form.address} onChange={e=>setForm({...form, address:e.target.value})} className="inp"/></Field></div>
        <div className="md:col-span-2"><Field label="Description" testId="store-desc-input"><textarea rows={3} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="inp"/></Field></div>
        <Field label="Delivery fee (₹)" testId="store-fee"><input value={form.delivery_fee} onChange={e=>setForm({...form, delivery_fee:e.target.value})} className="inp"/></Field>
        <Field label="Minimum order (₹)" testId="store-min"><input value={form.min_order} onChange={e=>setForm({...form, min_order:e.target.value})} className="inp"/></Field>
        <div className="md:col-span-2"><Field label="Store cover photo" testId="store-image"><ImageUploader testId="store-cover-uploader" label="cover photo" value={form.image_url} onChange={(url) => setForm({...form, image_url: url})}/></Field></div>
        <div className="md:col-span-2">
          <button data-testid="create-store-button" disabled={busy} onClick={submit} className="btn-primary w-full">{busy ? 'Creating…' : (termsAcceptedVersion ? 'Create store' : 'Accept agreement & continue')} <ArrowRight className="w-4 h-4"/></button>
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen}/>
      <TermsModal open={termsOpen} onOpenChange={setTermsOpen} onAccepted={(r) => setTermsAcceptedVersion(r.version)}/>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#E67E22;box-shadow:0 0 0 3px rgba(230,126,34,0.15)}`}</style>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-ink/70 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
